import React, { useEffect } from 'react';
import { Box, Form, Heading, useAnalytics } from '@medely/ui-kit';
import { useWorkExperience } from '../../hooks/useWorkExperience';
import { createWorkExperienceValidation } from '../schema';
import { WorkExperienceForm } from '../WorkExperienceForm';
import { useWorkExperienceMutations } from '../../mutations';
import { SetPageProps, FormValuesProps } from '../../interface';
import {
  AddMoreMonthsOfWorkExperienceCurrentUserGql,
  AddMoreMonthsOfWorkExperienceWorkHistoryGql,
} from './addMoreMonthsOfWorkExperienceWorkHistoryQueries';
import { useCurrentUser } from '@mp/hooks';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { ApplicationButtons } from '@mp/pages/application/components/ApplicationButtons';
import { LoadingState } from '@mp/pages/application/components/LoadingState';
import { FixedFooterLayout } from '@mp/components';

type AddMoreMonthsOfWorkExperienceProps = {
  setPage: (value: SetPageProps) => void;
  workHistories?: FragmentType<typeof AddMoreMonthsOfWorkExperienceWorkHistoryGql>[];
  currentUser?: FragmentType<typeof AddMoreMonthsOfWorkExperienceCurrentUserGql>;
  isLoading: boolean;
  isMobileVersion?: boolean;
};

const MIN_ENTRY_MONTHS = 1;

export const AddMoreMonthsOfWorkExperience = ({
  setPage,
  workHistories: inputWorkHistories,
  currentUser: inputCurrentUser,
  isLoading,
  isMobileVersion = false,
}: AddMoreMonthsOfWorkExperienceProps) => {
  const { proType } = useCurrentUser();
  const workHistories = parseFragmentData(
    AddMoreMonthsOfWorkExperienceWorkHistoryGql,
    inputWorkHistories,
  );
  const currentUser = parseFragmentData(
    AddMoreMonthsOfWorkExperienceCurrentUserGql,
    inputCurrentUser,
  );

  const analytics = useAnalytics();
  const { submitWorkExperienceInfo } = useWorkExperienceMutations();

  const { createPayload, getAddedMonthsFromMultiplesWorkHistories } = useWorkExperience({
    currentUser,
    //@ts-ignore
    workHistories,
  });

  const recentPositionData = currentUser?.professional?.professional_positions?.find(
    (p) => p.has_recent_experience,
  );

  const positionId = recentPositionData?.position.id;
  const positionName = recentPositionData?.position.display_name;
  const crnPositionId = 25;
  const minMonthsNeeded = positionId === crnPositionId ? 6 : 8;
  const initialAddedMonths = getAddedMonthsFromMultiplesWorkHistories(positionId);
  const missingMonths = minMonthsNeeded - initialAddedMonths;

  const handleBack = () => {
    setPage('experienceSummary');
  };

  const handleSubmit = (values: FormValuesProps) => {
    const parsedValues = createPayload({ ...values, position_id: positionId });
    submitWorkExperienceInfo(parsedValues, {
      onSuccess: () => {
        setPage('loading');
      },
    });
  };

  useEffect(() => {
    analytics.track('Product Application Add More Experience - Viewed', {
      account_id: currentUser?.id,
      context: 'Product Application Add More Experience',
      subcontext: null,
      action: 'Viewed',
      pro_type: proType,
    });
  }, []);

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <>
      <Form handleSubmit={handleSubmit} schema={createWorkExperienceValidation(MIN_ENTRY_MONTHS)}>
        <FixedFooterLayout
          footerContent={<ApplicationButtons disabled={isLoading} backClick={handleBack} />}
          height="97%"
        >
          <Box pb={2}>
            <Heading
              size="s"
              color="text.primary"
              testId="work-experience-add-more-months-form-title"
              textAlign={isMobileVersion ? 'center' : 'left'}
            >
              Add at least {missingMonths} more months of recent {positionName} experience
            </Heading>
          </Box>

          <WorkExperienceForm
            currentUser={currentUser}
            positionId={positionId}
            hidePositionField={true}
            setPage={setPage}
          />
        </FixedFooterLayout>
      </Form>
    </>
  );
};
